import { useState } from "react";
import axios from "axios";
import Seo from "@/components/Seo";
import { Mail, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
    const [state, setState] = useState("idle");
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setState("submitting");
        setError("");
        try {
            await axios.post(`${API}/contact`, form);
            setState("sent");
            setForm({ name: "", email: "", subject: "", message: "", website: "" });
        } catch (err) {
            setState("error");
            if (err?.response?.status === 429) {
                setError("You've sent too many messages. Please wait a few minutes and try again.");
            } else {
                setError(err?.response?.data?.detail || "Something went wrong. Try again?");
            }
        }
    };

    return (
        <>
            <Seo
                title="Contact FinCalc"
                description="Get in touch with the FinCalc team. Feedback, calculator requests, and corrections welcome."
                canonical="/contact"
            />
            <section className="container-editorial py-16 max-w-3xl">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Get in touch</div>
                <h1 className="font-serif text-5xl lg:text-6xl text-primary mt-3 leading-tight">
                    Spotted a bug?<br />
                    <span className="italic">Tell us.</span>
                </h1>
                <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
                    Calculator corrections, new calculator requests, press inquiries — we read
                    every message, usually within a few business days.
                </p>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-8">
                    <div className="md:col-span-2 space-y-6">
                        <div className="flex gap-3">
                            <Mail className="w-5 h-5 text-accent mt-0.5" strokeWidth={1.5} />
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                                    Email
                                </div>
                                <a
                                    href="mailto:hello@afincalc.com"
                                    className="text-primary hover:text-accent"
                                    data-testid="contact-email-link"
                                >
                                    hello@afincalc.com
                                </a>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={submit} className="md:col-span-3 space-y-4" data-testid="contact-form" noValidate aria-label="Contact form">
                        {/* Honeypot: hidden from real users via CSS + aria-hidden.
                            Bots auto-fill it; backend silently discards those submissions. */}
                        <div
                            aria-hidden="true"
                            style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
                        >
                            <label htmlFor="website">Website</label>
                            <input
                                type="text"
                                id="website"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">
                                    Name
                                </label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full h-12 px-3 rounded-md bg-background border border-border font-mono text-base focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                                    data-testid="contact-input-name"
                                />
                            </div>
                            <div>
                                <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">
                                    Email
                                </label>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full h-12 px-3 rounded-md bg-background border border-border font-mono text-base focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                                    data-testid="contact-input-email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">
                                Subject
                            </label>
                            <input
                                required
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                className="w-full h-12 px-3 rounded-md bg-background border border-border font-mono text-base focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                                data-testid="contact-input-subject"
                            />
                        </div>
                        <div>
                            <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">
                                Message
                            </label>
                            <textarea
                                required
                                rows={6}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className="w-full p-3 rounded-md bg-background border border-border font-sans text-[15px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                                data-testid="contact-input-message"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={state === "submitting"}
                            data-testid="contact-submit"
                            className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-primary text-primary-foreground hover:bg-secondary transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-medium text-sm"
                        >
                            {state === "submitting" ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                            ) : state === "sent" ? (
                                <><Check className="w-4 h-4" /> Sent. Thanks!</>
                            ) : (
                                "Send message"
                            )}
                        </button>
                        {error && <p className="text-sm text-destructive" data-testid="contact-error" role="alert" aria-live="assertive">{error}</p>}
                    </form>
                </div>
            </section>
        </>
    );
}
